import { useMutation, useQueryClient } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { sendToApi } from 'api/client'
import { FontRole, FontStyle, TextStyle } from 'types'
import { buildErrorMessage } from 'utils'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { reportError } from 'utils/error'
import { snakeCase } from 'lodash'
import produce from 'immer'

export interface Variables {
  style: Partial<FontStyle>
  name?: string
}

interface Props {
  brandVisualKitId: number
  fontRole: FontRole
}

interface Data {
  text_style: TextStyle
}

interface Context {
  previousTextStyles?: { text_styles: Partial<TextStyle>[] }
}

export const upsertTextStyle = async ({
  workspaceId,
  brandVisualKitId,
  fontRole,
  ...variables
}: Props & Variables & { workspaceId: number }) => {
  const response = await sendToApi<Data>(
    `/w/${workspaceId}/brand_visual_kits/${brandVisualKitId}/text_styles?font_role=${snakeCase(fontRole)}`,
    {
      method: 'PUT',
      body: { ...variables },
    },
  )

  if (response.type !== SUCCESS_RESPONSE_TYPE) {
    reportError('Failed to update text style', {
      metaData: {
        fontRole,
        variables,
        response: response.data,
      },
    })
    throw new Error(
      buildErrorMessage(response.data.errors) ||
        'An error occurred! Our team has been notified and we are looking into it.',
    )
  }
  return response.data
}

export const useUpsertTextStyle = ({ brandVisualKitId, fontRole }: Props) => {
  const currentWorkspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()

  return useMutation<Data, Error, Variables, Context>(
    ['workspaces', currentWorkspaceId, 'brand_visual_kits', brandVisualKitId, 'text_style', fontRole],
    (variables) => {
      return upsertTextStyle({ fontRole, workspaceId: currentWorkspaceId, brandVisualKitId, ...variables })
    },
    {
      onMutate: async (variables) => {
        await queryClient.cancelQueries([
          'workspaces',
          currentWorkspaceId,
          'brand_visual_kits',
          brandVisualKitId,
          'text_styles',
          'list',
        ])
        const previousTextStyles = queryClient.getQueryData([
          'workspaces',
          currentWorkspaceId,
          'brand_visual_kits',
          brandVisualKitId,
          'text_styles',
          'list',
        ]) as { text_styles: Partial<TextStyle>[] } | undefined
        queryClient.setQueryData<{ text_styles: Partial<TextStyle>[] } | undefined>(
          ['workspaces', currentWorkspaceId, 'brand_visual_kits', brandVisualKitId, 'text_styles', 'list'],
          (old) => {
            return produce(old, (draft) => {
              if (!draft) {
                return
              }

              const textStyle = draft.text_styles.find((textStyle) => textStyle.font_role === snakeCase(fontRole))

              draft.text_styles = [
                ...(previousTextStyles?.text_styles.filter(
                  (textStyle) => textStyle.font_role !== snakeCase(fontRole),
                ) || []),
                { ...textStyle, ...variables },
              ]
            })
          },
        )
        return { previousTextStyles }
      },
      onError: (_, __, context) => {
        queryClient.setQueryData(
          ['workspaces', currentWorkspaceId, 'brand_visual_kits', brandVisualKitId, 'text_styles', 'list'],
          context?.previousTextStyles,
        )
      },
      onSettled: () => {
        queryClient.invalidateQueries(['workspaces', currentWorkspaceId, 'brand_visual_kits'])
      },
    },
  )
}
